import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_PREFS,
  applyPrefs,
  loadPrefs,
  savePrefs,
  type A11yPrefs,
} from "@/lib/a11yPrefs";

interface A11yContextValue {
  prefs: A11yPrefs;
  setPref: <K extends keyof A11yPrefs>(key: K, value: A11yPrefs[K]) => void;
  reset: () => void;
}

const AccessibilityContext = createContext<A11yContextValue | null>(null);

/**
 * Holds user accessibility preferences, applies them to <html>, and persists
 * them to localStorage.
 *
 * First render (server AND client) uses DEFAULT_PREFS so hydration matches the
 * pre-rendered markup; real prefs are loaded from localStorage in an effect
 * after mount. The <html> hooks are already correct pre-paint thanks to the
 * inline script in index.html, so this loading step is invisible.
 */
export function AccessibilityProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<A11yPrefs>(DEFAULT_PREFS);
  const [hydrated, setHydrated] = useState(false);

  // Load persisted prefs once, after mount (client only).
  useEffect(() => {
    setPrefs(loadPrefs());
    setHydrated(true);
  }, []);

  // Re-apply + persist on every change (but not before we've loaded, or we'd
  // clobber the inline script's pre-paint work with defaults).
  useEffect(() => {
    if (!hydrated) return;
    applyPrefs(document.documentElement, prefs);
    savePrefs(prefs);
  }, [prefs, hydrated]);

  // Keep "system" theme in sync with live OS changes.
  useEffect(() => {
    if (!hydrated || prefs.theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyPrefs(document.documentElement, prefs);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [hydrated, prefs]);

  const setPref = useCallback<A11yContextValue["setPref"]>((key, value) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => setPrefs(DEFAULT_PREFS), []);

  const value = useMemo(() => ({ prefs, setPref, reset }), [prefs, setPref, reset]);

  return (
    <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>
  );
}

export function useA11y(): A11yContextValue {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error("useA11y must be used within <AccessibilityProvider>");
  return ctx;
}
